import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { COLUMNS } from "./columns";
import {
  useTable,
  useRowSelect,
  useExpanded,
  usePagination
} from "react-table";
import {
  noDataPresent,
  responseStatus,
  tooltiptextId
} from "../../../../../../../locales/en";
import TableSkeleton from "../../../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../../../hooks/useVisibleColumnCount";
import { useDispatch, useSelector } from "react-redux";
import "./../../../../reportable/reportable.scss";
import { Link } from "react-router-dom";
import ExpandToggle from "../../../../../../common/ExpandToggle";
import { Tooltip } from "react-tooltip";
import DualPagination from "../../../../../../common/DualPagination";
import { INVOICE_DETAILS } from "../../../../../../../route/paths";
import { getUserKPIReportModalData } from "../../../../../../../redux/userReportsSlice";
import { cleanQuoteId, showToast } from "../../../../../../../utils/utils";
import {
  PAGE_INDEX,
  PAGE_SIZE,
  showToastType
} from "../../../../../../../utils/constants/appConstants";

const InvoiceReportModalTable = (props) => {
  const dispatch = useDispatch();
  const { invoiceIdsListRef, filterType } = props;
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const initialCustomerModalData = null;
  const { kpiReportModalDataLoader } = useSelector((state) => state.userReport);
  const [customerModalData, setCustomerModalData] = useState(
    initialCustomerModalData
  );
  const columns = useMemo(() => COLUMNS, []);

  const invoiceDataIds = invoiceIdsListRef.current;

  const tableData = useMemo(
    () =>
      Array.isArray(customerModalData?.invoiceDetails)
        ? customerModalData.invoiceDetails
        : [],
    [customerModalData?.invoiceDetails]
  );

  const [expandedRows, setExpandedRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data: tableData,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },

    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...columns]);
    }
  );

  const { pageIndex } = state;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  useEffect(() => {
    const handleInvoiceModalData = async () => {
      try {
        const paramsData = {
          ids: invoiceDataIds,
          filterType,
          page: pageIndex,
          size: pageSize
        };
        const response = await dispatch(
          getUserKPIReportModalData(paramsData)
        ).unwrap();
        if (response) {
          setCustomerModalData(response?.data || response);
          setTotalPages(response?.pageInfo?.totalPages || 0);
        }
      } catch (error) {
        showToast(
          error.response?.data?.message || responseStatus.errorText,
          showToastType.error
        );
      }
    };
    handleInvoiceModalData();
  }, [invoiceDataIds, pageIndex, pageSize, dispatch]);

  return (
    <div className="col-md-12">
      <table
        className={`mt-2 mb-3 reportable1 inv-monthly-table bgless_table`}
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} className="parentrow" {...rest}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th key={column.id} {...column.getHeaderProps()}>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {kpiReportModalDataLoader && (
            <TableSkeleton rows={10} columns={visibleColumnCount} />
          )}
          {!kpiReportModalDataLoader && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!kpiReportModalDataLoader &&
            rows.length > 0 &&
            rows.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <ExpandToggle
                        isExpanded={isExpanded}
                        onToggle={() => toggleRow(row.id)}
                        id={tooltiptextId.monthlyKpiReportId}
                      />
                    </td>

                    {row.cells.map((cell) => {
                      let content = cell.render("Cell");
                      if (cell.column.id === "fundTapQuoteId") {
                        const cleanId = cleanQuoteId(
                          cell.row.original.fundTapQuoteId
                        );
                        content = (
                          <Link to={INVOICE_DETAILS(cleanId)}>
                            {cell.render("Cell")}
                          </Link>
                        );
                      }
                      return (
                        <td
                          key={cell.getCellProps().key}
                          {...cell.getCellProps()}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => {
                            let value = row.original[column.accessor];
                            if (
                              column.accessor === "dateCompleted" &&
                              (value === null || value === undefined)
                            ) {
                              value = "";
                            }
                            if (
                              ["invFunded", "invoiceFee", "amountDue"].includes(
                                column.accessor
                              )
                            ) {
                              value = `$${Number(value).toFixed(2)}`;
                            }
                            return (
                              <span
                                key={column.accessor}
                                data-label={column.Header}
                              >
                                {String(value ?? "")}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      {tableData && tableData.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={tableData.length}
        />
      )}
      <Tooltip id={tooltiptextId.monthlyKpiReportId} place="top" />
    </div>
  );
};

InvoiceReportModalTable.propTypes = {
  invoiceIdsListRef: PropTypes.object,
  filterType: PropTypes.string
};
export default InvoiceReportModalTable;
